/**
 * historyConverter — Converts loaded session history records into AiChatMessage
 * objects suitable for rendering in the chat UI.
 */

import type { AiChatMessage, AiChatContentSegment, AiChatToolCall } from '../types';

type HistoryBlock =
  | { type: 'text'; text: string }
  | { type: 'thinking'; thinking: string }
  | { type: 'tool_use'; id: string; name: string; input: Record<string, unknown> }
  | { type: 'tool_result'; toolUseId: string; content: string; isError: boolean };

type HistoryMessage = {
  role: 'user' | 'assistant';
  content: string;
  blocks: HistoryBlock[];
  timestamp: string;
};

/** Convert loaded history messages into AiChatMessage objects for the store. */
export function convertHistoryToMessages(history: HistoryMessage[]): AiChatMessage[] {
  // Build a map of toolUseId → result for pairing tool calls with their results
  const toolResults = new Map<string, { content: string; isError: boolean }>();
  for (const msg of history) {
    for (const block of msg.blocks) {
      if (block.type === 'tool_result') {
        toolResults.set(block.toolUseId, { content: block.content, isError: block.isError });
      }
    }
  }

  const messages: AiChatMessage[] = [];

  for (const msg of history) {
    // Skip user messages that only contain tool_result blocks (these are internal)
    if (msg.role === 'user' && msg.blocks.every((b) => b.type === 'tool_result')) continue;

    const segments: AiChatContentSegment[] = [];
    let textContent = '';

    for (const block of msg.blocks) {
      switch (block.type) {
        case 'text':
          segments.push({ type: 'text', text: block.text });
          textContent += block.text;
          break;
        case 'thinking':
          segments.push({ type: 'thinking', thinking: block.thinking, isStreaming: false });
          break;
        case 'tool_use': {
          const result = toolResults.get(block.id);
          const toolCall: AiChatToolCall = {
            id: block.id,
            name: block.name,
            input: block.input,
            isRunning: false,
            result: result ?? undefined,
          };
          segments.push({ type: 'tool_use', toolCall });
          break;
        }
        // tool_result blocks are handled above via the toolResults map
      }
    }

    // Use content from blocks or fall back to the raw content string
    const content = textContent || msg.content;

    messages.push({
      id: `hist-${messages.length}-${Date.now()}`,
      role: msg.role,
      content,
      segments: segments.length > 0 ? segments : [{ type: 'text', text: content }],
      timestamp: msg.timestamp ? new Date(msg.timestamp).getTime() : Date.now(),
    });
  }

  return messages;
}
